export const toJsDate = (triplet) => {
  if (!triplet) return null;
  const d = parseInt(triplet.day, 10);
  const m = parseInt(triplet.month, 10);
  const y = parseInt(triplet.year, 10);
  if (!d || !m || !y) return null;
  if (y < 1900 || y > 2200) return null;
  const date = new Date(y, m - 1, d);
  if (isNaN(date.getTime())) return null;
  return date;
};

const monthsBetween = (start, end) => {
  if (!start || !end) return 0;
  let months = (end.getFullYear() - start.getFullYear()) * 12;
  months += end.getMonth() - start.getMonth();
  if (end.getDate() < start.getDate()) months -= 1;
  return Math.max(0, months);
};

// Older helper that just sums the duration of every interval (kept for
// back-compat). For new code prefer `coverageAnalysis` which also detects
// gaps and is gap-aware.
export const coverageMonths = (entries) => {
  if (!entries || entries.length === 0) return 0;
  const now = new Date();
  let total = 0;
  entries.forEach((entry) => {
    const start = toJsDate(entry.startDate);
    const end = entry.endDate ? toJsDate(entry.endDate) : now;
    if (start && end && end > start) {
      total += monthsBetween(start, end);
    }
  });
  return total;
};

// Treat two intervals as adjacent when the next one starts within this many
// days of the previous one's end. Anything bigger counts as a gap.
const GAP_TOLERANCE_DAYS = 31;

const dayMs = 24 * 60 * 60 * 1000;

const formatGapDate = (d) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

// Analyse a list of entries and return { months, hasGap, gaps, mostRecentEnd,
// coversToNow }. `entries` is the same shape as for coverageMonths -
// { startDate, endDate } where endDate=null/undefined means "ongoing /
// current".
export const coverageAnalysis = (entries) => {
  const now = new Date();
  const empty = {
    months: 0,
    hasGap: false,
    gaps: [],
    mostRecentEnd: null,
    coversToNow: false,
  };
  if (!entries || entries.length === 0) return empty;

  const ranges = entries
    .map((entry) => {
      const start = toJsDate(entry.startDate);
      const isOngoing = !entry.endDate;
      const end = isOngoing ? now : toJsDate(entry.endDate);
      if (!start || !end || end <= start) return null;
      return { start, end, isOngoing };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);

  if (ranges.length === 0) return empty;

  // Merge overlapping or near-adjacent ranges (within GAP_TOLERANCE_DAYS).
  const merged = [];
  ranges.forEach((r) => {
    const last = merged[merged.length - 1];
    if (last && r.start - last.end <= GAP_TOLERANCE_DAYS * dayMs) {
      last.end = r.end > last.end ? r.end : last.end;
      last.isOngoing = last.isOngoing || r.isOngoing;
    } else {
      merged.push({ ...r });
    }
  });

  const gaps = [];
  for (let i = 1; i < merged.length; i += 1) {
    const prev = merged[i - 1];
    const cur = merged[i];
    if (cur.start - prev.end > GAP_TOLERANCE_DAYS * dayMs) {
      gaps.push({
        from: formatGapDate(prev.end),
        to: formatGapDate(cur.start),
        months: monthsBetween(prev.end, cur.start),
      });
    }
  }

  const last = merged[merged.length - 1];
  const coversToNow =
    last.isOngoing || now - last.end <= GAP_TOLERANCE_DAYS * dayMs;
  if (!coversToNow) {
    gaps.push({
      from: formatGapDate(last.end),
      to: formatGapDate(now),
      months: monthsBetween(last.end, now),
    });
  }

  let months = 0;
  merged.forEach((r) => {
    months += monthsBetween(r.start, r.end);
  });

  return {
    months,
    hasGap: gaps.length > 0,
    gaps,
    mostRecentEnd: last.end,
    coversToNow,
  };
};

export const formatCoverage = (months) => {
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return `${m} month${m === 1 ? '' : 's'}`;
  if (m === 0) return `${y} year${y === 1 ? '' : 's'}`;
  return `${y}y ${m}m`;
};
